import Image from 'next/legacy/image';
import { LeagueEntry } from '@api-service/mini-leagues';
import {
  BodyRegular,
  BodySmallRegular,
  BodySmallRegularBold,
  H3
} from '@components/atoms/typography';
import Button from '@components/atoms/button';
import { BLANK_SHIRT_PATH } from '@lib/utils';
import { useTeamKitImages } from '@hooks/useTeamKitImages';
import {
  resolveEntrySquad,
  type EntrySquadRow
} from '@lib/page-utils/MiniLeagues/resolve-entry-squad';

type MiniLeagueTeamCardProps = {
  entry: LeagueEntry | null;
  onClose: () => void;
  // Set while the squad is read separately, for a league sent without squads.
  // `entry` still names the team meanwhile.
  isSquadLoading?: boolean;
  isSquadError?: boolean;
  onRetry?: () => void;
};

const RosterRow = ({ row }: { row: EntrySquadRow }) => {
  const { resolveTeamKit } = useTeamKitImages();
  const { player } = row;
  // The backend has a real goalkeeper kit per team; the bundled set had one
  // generic GK shirt for all twenty.
  const remoteKit = resolveTeamKit(
    player.team.shortName,
    player.position === 'GK'
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div className="w-8">
          <BodySmallRegularBold textColor="text-neutral-600">
            {player.position}
          </BodySmallRegularBold>
        </div>
        <div className="h-8 w-8 mx-1 relative">
          <Image
            src={remoteKit ?? BLANK_SHIRT_PATH}
            alt={`${player.team.shortName} shirt`}
            width={32}
            height={32}
            unoptimized
          />
        </div>
        <div className="flex">
          <BodyRegular className="mb-0">{player.displayName}</BodyRegular>
          <div className="mx-1">
            {row.isCaptain ? (
              <Image
                src={'/images/captain-armband.png'}
                height={14}
                width={14}
                alt="captain-armband"
              />
            ) : row.isViceCaptain ? (
              <Image
                src={'/images/vice-captain-armband.png'}
                height={14}
                width={14}
                alt="vice-captain-armband"
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="px-2 py-1 rounded-lg">
        <BodySmallRegular>
          {row.displayPoints} point{row.displayPoints === 1 ? '' : 's'}
        </BodySmallRegular>
      </div>
    </div>
  );
};

const MiniLeagueTeamCard = ({
  entry,
  onClose,
  isSquadLoading = false,
  isSquadError = false,
  onRetry
}: MiniLeagueTeamCardProps) => {
  if (!entry) return null;

  const isSquadPending = isSquadLoading || isSquadError;
  // The entry's lineup is the team as it was set up; its gameweek total already
  // includes live auto-subs, so show the squad the same way.
  const squad = isSquadPending ? null : resolveEntrySquad(entry);

  return (
    <div className="overflow-y-scroll h-[80vh] min-w-[90vw] md:min-w-[500px] pt-3 px-3 md:px-5 pb-5 md:p-8 md:px-10 pt-5">
      <div className="flex items-center mt-5 md:mt-3 justify-between px-2 md:px-0">
        <div>
          <H3 className="text-lg md:text-2xl">{entry.teamName}</H3>
          <BodySmallRegular textColor="text-neutral-600">
            {entry.playerName}
          </BodySmallRegular>
        </div>
      </div>

      {isSquadLoading && (
        <BodyRegular className="mt-5 text-center" data-testid="squad-loading">
          Loading squad...
        </BodyRegular>
      )}
      {isSquadError && !isSquadLoading && (
        <div
          className="mt-5 flex flex-col items-center gap-3"
          data-testid="squad-error"
        >
          <BodyRegular>We couldn&apos;t load this squad.</BodyRegular>
          {onRetry && (
            <Button className="px-5" onClick={onRetry} variant="secondary">
              Try again
            </Button>
          )}
        </div>
      )}

      {squad && (
        <div className="mt-3">
          {squad.starting.map((row, index) => (
            <div
              className="my-2"
              key={`starting-${row.player.displayName}-${index}`}
            >
              <RosterRow row={row} />
            </div>
          ))}
          <hr className="my-3" />
          {squad.bench.map((row, index) => (
            <div
              className="my-2"
              key={`bench-${row.player.displayName}-${index}`}
            >
              <RosterRow row={row} />
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-5">
        <Button className="px-5" onClick={onClose} variant="primary">
          Close
        </Button>
      </div>
    </div>
  );
};

export default MiniLeagueTeamCard;
